import { create } from "zustand";
import { getSupportedMimeType } from "@/components/pages/Home/InPersonMeetingPage/helpers/supported-mime-type";
import { sendLog } from "@/api";
import { LogLevel } from "@/types";

let mediaRecorder: MediaRecorder | null = null;
let globalStream: MediaStream | null = null;
let recordingInProgress = false;
let selectedDevice: MediaDeviceInfo | undefined;
let emptyChunkLogged = false;
let foregroundReturnListener: (() => void) | null = null;
let deviceChangeListener: (() => void) | null = null;

// State for auto-restart on iOS background kill
let activeChunkCallback: ((chunk: Blob) => void) | null = null;
let activeDeviceId: string | undefined;
let isRestarting = false;

// TEMP: diagnostic instrumentation for iOS Safari "no audio captured" bug.
// Captures the lifecycle of getUserMedia → MediaRecorder.start → pause/resume
// → first chunks so we can identify which boundary actually fails.
// Remove once root cause is confirmed.
type DiagSession = {
  readonly id: number;
  recorderStartTs: number;
  chunkLogCount: number;
};
let diagSessionCounter = 0;
// Used by pause/resume which run from stable top-level functions and need to
// look up the in-flight session; cleared in stopMediaRecorder.
let activeDiagSession: DiagSession | null = null;
const sanitizeTrack = (track?: MediaStreamTrack | null) => ({
  trackMuted: track ? String(track.muted) : "no-track",
  trackReadyState: track?.readyState || "no-track",
  trackEnabled: track ? String(track.enabled) : "no-track",
  hasTrackLabel: track ? String(!!track.label) : "no-track",
});
const browserCategory = () => {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|Android/.test(ua);
  return `iOS=${isIOS}|android=${isAndroid}|safari=${isSafari}`;
};
const logRecordingDiag = (
  session: DiagSession | null,
  label: string,
  data: Record<string, string> = {},
) => {
  sendLog({
    level: LogLevel.INFO,
    message: `[recording-diag] ${label}`,
    component: "useRecord",
    data: {
      diagSessionId: session ? String(session.id) : "n/a",
      ...data,
    },
  });
};

function attachRecorderListeners(
  recorder: MediaRecorder,
  cb: (chunk: Blob) => void,
  diagSession: DiagSession | null,
) {
  // TEMP: diagnostic — capture the actual recorder-running moment. On iOS Safari
  // there can be a gap between calling .start() and the recorder transitioning
  // to "recording"; this listener also fires on restartMediaRecorder so timing
  // resets correctly after a background-kill recovery.
  recorder.addEventListener("start", () => {
    if (diagSession) {
      diagSession.recorderStartTs = performance.now();
      const track = recorder.stream?.getAudioTracks()?.[0];
      logRecordingDiag(diagSession, "MediaRecorder start event", {
        recorderState: recorder.state,
        ...sanitizeTrack(track),
      });
    }
  });

  recorder.addEventListener("dataavailable", (e) => {
    if (diagSession && diagSession.chunkLogCount < 5) {
      const track = recorder.stream?.getAudioTracks()?.[0];
      logRecordingDiag(diagSession, "dataavailable chunk", {
        chunkIndex: String(diagSession.chunkLogCount),
        size: String(e.data?.size ?? 0),
        recorderState: recorder.state,
        sinceRecorderStartMs: diagSession.recorderStartTs
          ? String(Math.round(performance.now() - diagSession.recorderStartTs))
          : "n/a",
        ...sanitizeTrack(track),
      });
      diagSession.chunkLogCount++;
    }

    if (e.data && e.data.size > 0) {
      emptyChunkLogged = false; // reset so we log again if empty chunks reappear
      cb(e.data);
    } else if (!emptyChunkLogged) {
      emptyChunkLogged = true;
      const target = e.target as MediaRecorder;
      sendLog({
        level: LogLevel.WARN,
        message: "MediaRecorder dataavailable with empty chunk",
        component: "useRecord",
        data: {
          size: String(e.data.size),
          recorderState: target?.state || "unknown",
          documentVisibility: document.visibilityState,
        },
      });
    }
  });

  // Fires if iOS explicitly kills the recorder
  recorder.addEventListener("error", (e) => {
    const recorderError = (e as Event & { error?: DOMException }).error;
    const target = e.target as MediaRecorder;
    sendLog({
      level: LogLevel.ERROR,
      message: "MediaRecorder error event",
      component: "useRecord",
      data: {
        error: recorderError?.message || "Unknown",
        errorName: recorderError?.name || "Unknown",
        recorderState: target?.state || "unknown",
        documentVisibility: document.visibilityState,
      },
    });
  });
}

async function restartMediaRecorder() {
  if (!activeChunkCallback || !recordingInProgress || isRestarting) return;
  isRestarting = true;

  try {
    const tracksAlive = globalStream
      ?.getTracks()
      .some((t) => t.readyState === "live");
    if (!tracksAlive) {
      globalStream?.getTracks().forEach((t) => t.stop());
      const constraints: MediaStreamConstraints = {
        audio: activeDeviceId ? { deviceId: { exact: activeDeviceId } } : true,
      };
      globalStream = await navigator.mediaDevices.getUserMedia(constraints);
    }

    // Re-check after async gap — user may have tapped Stop while getUserMedia was in flight
    if (!recordingInProgress || !activeChunkCallback || !globalStream) return;

    const mimeType = getSupportedMimeType();
    if (!mimeType) return;
    mediaRecorder = new MediaRecorder(globalStream, { mimeType });
    attachRecorderListeners(mediaRecorder, activeChunkCallback, activeDiagSession);
    mediaRecorder.start(5000);
    emptyChunkLogged = false;

    useRecord.getState().setMediaRecorder(mediaRecorder);

    sendLog({
      level: LogLevel.WARN,
      message: "MediaRecorder restarted after iOS background kill",
      component: "useRecord",
      data: { deviceId: activeDeviceId || "default" },
    });
  } catch (error) {
    sendLog({
      level: LogLevel.ERROR,
      message: "Failed to restart MediaRecorder after iOS background kill",
      component: "useRecord",
      data: { error: (error as Error)?.message || "" },
    });
  } finally {
    isRestarting = false;
  }
}

async function switchRecordingDevice(deviceId: string) {
  if (!activeChunkCallback || !recordingInProgress) return;
  if (deviceId === activeDeviceId) return;
  // Reject rather than silently no-op so the caller rolls back its
  // optimistic device selection.
  if (isRestarting) {
    throw new Error("Another recorder restart is already in progress");
  }
  // Pausing a just-started recorder is unreliable on iOS Safari (see the
  // start-chime flow, which skips its pause cycle there), so switching while
  // paused is unsupported; the UI disables the selector while paused.
  if (useRecord.getState().isPaused) {
    throw new Error("Cannot switch microphone while paused");
  }
  isRestarting = true;

  try {
    // Acquire the new mic before touching the live recorder so a failed
    // switch leaves the recording running on the previous device.
    const newStream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: { exact: deviceId } },
    });

    // Re-check after async gap — user may have tapped Stop while getUserMedia was in flight
    if (!recordingInProgress || !activeChunkCallback) {
      newStream.getTracks().forEach((t) => t.stop());
      return;
    }

    const mimeType = getSupportedMimeType();
    if (!mimeType) {
      newStream.getTracks().forEach((t) => t.stop());
      return;
    }

    // Resolve the new device's info (used only for log labels) before tearing
    // down the old recorder — a failed lookup must not leave recording stopped.
    let newDeviceInfo: MediaDeviceInfo | undefined;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      newDeviceInfo = devices.find(({ deviceId: id }) => id === deviceId);
    } catch {
      newDeviceInfo = undefined;
    }

    const oldRecorder = mediaRecorder;
    const oldStream = globalStream;

    // stop() flushes the old recorder's final chunk; wait for it so chunks
    // reach the callback in order before the new recorder starts producing.
    if (oldRecorder && oldRecorder.state !== "inactive") {
      await new Promise<void>((resolve) => {
        oldRecorder.addEventListener("stop", () => resolve(), { once: true });
        oldRecorder.stop();
      });
    }
    oldStream?.getTracks().forEach((t) => t.stop());

    if (!recordingInProgress || !activeChunkCallback) {
      newStream.getTracks().forEach((t) => t.stop());
      return;
    }

    globalStream = newStream;
    activeDeviceId = deviceId;
    selectedDevice = newDeviceInfo;

    mediaRecorder = new MediaRecorder(newStream, { mimeType });
    attachRecorderListeners(
      mediaRecorder,
      activeChunkCallback,
      activeDiagSession,
    );
    mediaRecorder.start(5000);
    emptyChunkLogged = false;

    useRecord.getState().setMediaRecorder(mediaRecorder);

    sendLog({
      level: LogLevel.INFO,
      message: "Recording input device switched mid-recording",
      component: "useRecord",
      data: { deviceLabel: selectedDevice?.label || "unknown" },
    });
  } catch (error) {
    sendLog({
      level: LogLevel.ERROR,
      message: "Failed to switch recording input device",
      component: "useRecord",
      data: { error: (error as Error)?.message || "" },
    });
    throw error;
  } finally {
    isRestarting = false;
  }
}

async function initRecording(
  chunkCallback: (chunk: Blob) => void,
  timeslice = 15000, // 15s chunk intervals
  deviceId?: string,
) {
  if (recordingInProgress) return; // avoid double-start
  if (!window.MediaRecorder) {
    // fallback to an AudioWorklet approach or throw error
    throw new Error("MediaRecorder not supported");
  }

  const mimeType = getSupportedMimeType();
  if (!mimeType) {
    // fallback again
    throw new Error("No supported audio mime types on this browser");
  }

  // Get mic stream with optional device ID
  const constraints: MediaStreamConstraints = {
    audio: deviceId ? { deviceId: { exact: deviceId } } : true,
  };

  // Need to store selected device to use it label
  const devices = await navigator.mediaDevices.enumerateDevices();
  selectedDevice = devices.find(({ deviceId: id }) => id === deviceId);

  // TEMP: diagnostic — per-session state captured in closures so late-firing
  // track events (mute/unmute/ended) and dataavailable chunks log under the
  // session they belong to, not whichever session happens to be active now.
  const diagSession: DiagSession = {
    id: ++diagSessionCounter,
    recorderStartTs: 0,
    chunkLogCount: 0,
  };
  activeDiagSession = diagSession;
  const diagGumStart = performance.now();
  logRecordingDiag(diagSession, "getUserMedia called", {
    hasDeviceId: String(!!deviceId),
    browserCategory: browserCategory(),
  });

  globalStream = await navigator.mediaDevices.getUserMedia(constraints);

  // Save active recording params for potential restart
  activeChunkCallback = chunkCallback;
  activeDeviceId = deviceId;

  // TEMP: diagnostic — track state immediately post-gum, mute/unmute events
  const diagAudioTrack = globalStream.getAudioTracks()[0];
  if (diagAudioTrack) {
    logRecordingDiag(diagSession, "getUserMedia resolved", {
      gumDurationMs: String(Math.round(performance.now() - diagGumStart)),
      ...sanitizeTrack(diagAudioTrack),
    });
    diagAudioTrack.addEventListener("mute", () => {
      logRecordingDiag(diagSession, "track mute event", {
        sinceGumMs: String(Math.round(performance.now() - diagGumStart)),
        ...sanitizeTrack(diagAudioTrack),
      });
    });
    diagAudioTrack.addEventListener("unmute", () => {
      logRecordingDiag(diagSession, "track unmute event", {
        sinceGumMs: String(Math.round(performance.now() - diagGumStart)),
        ...sanitizeTrack(diagAudioTrack),
      });
    });
    diagAudioTrack.addEventListener("ended", () => {
      logRecordingDiag(diagSession, "track ended event", {
        sinceGumMs: String(Math.round(performance.now() - diagGumStart)),
        ...sanitizeTrack(diagAudioTrack),
      });
    });
  } else {
    logRecordingDiag(diagSession, "getUserMedia resolved without audio track", {
      gumDurationMs: String(Math.round(performance.now() - diagGumStart)),
    });
  }

  // handle case if user mic was changed and we have no audio
  // Remove any previous listener to avoid leaks across recordings
  if (deviceChangeListener) {
    navigator.mediaDevices.removeEventListener(
      "devicechange",
      deviceChangeListener,
    );
  }

  deviceChangeListener = async () => {
    const leftDevices = await navigator.mediaDevices.enumerateDevices();
    const leftAudioDevices = leftDevices.filter(
      (device) => device.kind === "audioinput" && device.deviceId !== "",
    );

    const isSelectedDeviceStillPresent = leftAudioDevices.some(
      ({ deviceId: id, label }) =>
        id === selectedDevice?.deviceId || label === selectedDevice?.label,
    );

    if (!isSelectedDeviceStillPresent) {
      sendLog({
        level: LogLevel.VERBOSE,
        message: "Devices have changed",
        component: "useRecord",
        data: {
          message: `Device with label '${selectedDevice?.label}' was removed`,
        },
      });
    }
  };

  navigator.mediaDevices.addEventListener("devicechange", deviceChangeListener);

  // Create MediaRecorder w/ Opus
  mediaRecorder = new MediaRecorder(globalStream, {
    mimeType,
    // audioBitsPerSecond: 32000 // optionally set a bitrate
  });

  attachRecorderListeners(mediaRecorder, chunkCallback, diagSession);

  // Fires when returning from background — reveals if iOS silently suspended the recorder
  foregroundReturnListener = async () => {
    if (document.visibilityState === "visible") {
      const state = mediaRecorder?.state || "null";

      sendLog({
        level: LogLevel.WARN,
        message:
          "Page returned to foreground during recording - MediaRecorder state check",
        component: "useRecord",
        data: {
          recorderState: state,
          recordingInProgress: String(recordingInProgress),
        },
      });

      // Don't restart if user deliberately paused before locking — would capture unwanted audio
      if (
        state === "inactive" &&
        recordingInProgress &&
        !useRecord.getState().isPaused
      ) {
        await restartMediaRecorder();
      }
    }
  };
  document.addEventListener("visibilitychange", foregroundReturnListener);

  // TEMP: diagnostic — capture state immediately before/after start.
  // recorderStartTs is set by the "start" event listener in attachRecorderListeners.
  logRecordingDiag(diagSession, "before MediaRecorder.start", {
    timesliceMs: String(timeslice),
    recorderState: mediaRecorder.state,
    ...sanitizeTrack(diagAudioTrack),
  });

  // Start recording, chunked
  mediaRecorder.start(timeslice);
  recordingInProgress = true;

  logRecordingDiag(diagSession, "after MediaRecorder.start", {
    recorderState: mediaRecorder.state,
    ...sanitizeTrack(diagAudioTrack),
  });
}

function stopMediaRecorder() {
  if (mediaRecorder && mediaRecorder.state !== "inactive") {
    mediaRecorder.stop(); // triggers final dataavailable event
    mediaRecorder.stream.getTracks().forEach((track) => track.stop());
  }
  if (globalStream) {
    globalStream.getTracks().forEach((track) => track.stop());
  }
  mediaRecorder = null;
  globalStream = null;
  recordingInProgress = false;
  emptyChunkLogged = false;
  activeChunkCallback = null;
  activeDeviceId = undefined;
  isRestarting = false;
  activeDiagSession = null;
  if (foregroundReturnListener) {
    document.removeEventListener("visibilitychange", foregroundReturnListener);
    foregroundReturnListener = null;
  }
  if (deviceChangeListener) {
    navigator.mediaDevices.removeEventListener(
      "devicechange",
      deviceChangeListener,
    );
    deviceChangeListener = null;
  }
}

function pauseMediaRecorder() {
  // TEMP: diagnostic — capture pause timing relative to recorder start
  const session = activeDiagSession;
  const prevState = mediaRecorder?.state || "null";
  const sinceStart = session?.recorderStartTs
    ? String(Math.round(performance.now() - session.recorderStartTs))
    : "n/a";
  if (mediaRecorder && mediaRecorder.state === "recording") {
    mediaRecorder.pause();
  }
  const track = mediaRecorder?.stream?.getAudioTracks()?.[0];
  logRecordingDiag(session, "pauseMediaRecorder", {
    prevState,
    newState: mediaRecorder?.state || "null",
    sinceRecorderStartMs: sinceStart,
    ...sanitizeTrack(track),
  });
}

function resumeMediaRecorder() {
  // TEMP: diagnostic — capture resume timing relative to recorder start
  const session = activeDiagSession;
  const prevState = mediaRecorder?.state || "null";
  const sinceStart = session?.recorderStartTs
    ? String(Math.round(performance.now() - session.recorderStartTs))
    : "n/a";
  if (mediaRecorder && mediaRecorder.state === "paused") {
    mediaRecorder.resume();
  }
  const track = mediaRecorder?.stream?.getAudioTracks()?.[0];
  logRecordingDiag(session, "resumeMediaRecorder", {
    prevState,
    newState: mediaRecorder?.state || "null",
    sinceRecorderStartMs: sinceStart,
    ...sanitizeTrack(track),
  });
}

/** Zustand store for your "record/pause/stop" logic. */
type RecordingState = {
  isRecording: boolean;
  isPaused: boolean;
  startRecording: (
    cb: (chunk: Blob) => void,
    deviceId?: string,
  ) => Promise<void>;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  /** Swap the live recording to another input device. Rejects (recording untouched) if the new device can't be acquired, another restart is in flight, or recording is paused. */
  switchDevice: (deviceId: string) => Promise<void>;
  uploadId: string | null;
  setUploadId: (id: string | null) => void;
  mediaRecorder: MediaRecorder | null;
  setMediaRecorder: (recorder: MediaRecorder | null) => void;
  /** True from stop/retry until the upload is finalized; isRecording is already false by then. */
  isFinalizing: boolean;
  setIsFinalizing: (value: boolean) => void;
};

export const useRecord = create<RecordingState>((set) => ({
  isRecording: false,
  isPaused: false,
  uploadId: null,
  mediaRecorder,
  isFinalizing: false,
  setIsFinalizing: (value) => set({ isFinalizing: value }),
  setUploadId: (id) => set({ uploadId: id }),
  setMediaRecorder: (recorder) => set({ mediaRecorder: recorder }),
  startRecording: async (cb, deviceId) => {
    await initRecording(cb, 5000 /* chunk interval ms */, deviceId);
    set({ isRecording: true, isPaused: false, mediaRecorder: mediaRecorder });
  },

  stopRecording: () => {
    stopMediaRecorder();
    set({ isRecording: false, isPaused: false, uploadId: null });
  },

  pauseRecording: () => {
    pauseMediaRecorder();
    set({ isPaused: true });
  },

  resumeRecording: () => {
    resumeMediaRecorder();
    set({ isPaused: false });
  },

  switchDevice: (deviceId) => switchRecordingDevice(deviceId),
}));
